import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { ListRow } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { FileCategoryChip } from '@/components/documents/FileCategoryChip';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import {
  patientReportTextErrorMessage,
  usePatientReportTextLibrary,
  type PatientReportTextType,
} from '@/hooks/usePatientReportTexts';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SectionCard, SettingsEmpty, SettingsLoading } from '@/pages/settings/settingsChrome';

export function ReportTextTypeSettings({
  clinicId,
  canEdit,
}: {
  clinicId: string | null;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const library = usePatientReportTextLibrary(clinicId);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PatientReportTextType | null>(null);
  const [name, setName] = useState('');
  const [singleVersion, setSingleVersion] = useState(false);
  const labels = {
    duplicate: t('patientReports.texts_duplicate'),
    cpr: t('patientReports.texts_cpr_blocked'),
    single: t('patientReports.texts_single'),
    singleBlocked: t('settings.integrations.patient_reports.text_types_single_blocked'),
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setSingleVersion(false);
    setShowForm(true);
  };

  const openEdit = (type: PatientReportTextType) => {
    setEditing(type);
    setName(type.name);
    setSingleVersion(type.singleVersion);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setName('');
    setSingleVersion(false);
  };

  const fail = (caught: unknown) => {
    toast.error(
      patientReportTextErrorMessage(
        caught,
        t('settings.integrations.patient_reports.text_types_save_error'),
        labels,
      ),
    );
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      if (editing) {
        await library.updateType({ id: editing.id, name, singleVersion });
        toast.success(t('settings.integrations.patient_reports.text_types_updated'));
      } else {
        await library.createType({ name, singleVersion });
        toast.success(t('settings.integrations.patient_reports.text_types_created'));
      }
      closeForm();
    } catch (caught) {
      fail(caught);
    }
  };

  const setFlag = async (type: PatientReportTextType, next: boolean) => {
    if (next === type.singleVersion) return;
    try {
      await library.updateType({ id: type.id, name: type.name, singleVersion: next });
      toast.success(t('settings.integrations.patient_reports.text_types_updated'));
    } catch (caught) {
      fail(caught);
    }
  };

  const remove = async (type: PatientReportTextType) => {
    if (!window.confirm(t('patientReports.texts_delete_type_confirm', { name: type.name }))) return;
    try {
      await library.deleteType(type.id);
      toast.success(t('settings.integrations.patient_reports.text_types_deleted'));
    } catch (caught) {
      fail(caught);
    }
  };

  return (
    <SectionCard
      title={t('settings.integrations.patient_reports.text_types_title')}
      subtitle={t('settings.integrations.patient_reports.text_types_hint')}
      readOnly={!canEdit}
      action={
        canEdit && library.types.length > 0 ? (
          <Button type="button" onClick={openCreate} className="w-full nav:w-auto">
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.integrations.patient_reports.text_types_new')}
          </Button>
        ) : undefined
      }
    >
      <div data-testid="report-text-type-settings">
        {library.isLoading ? (
          <SettingsLoading />
        ) : library.error ? (
          <p className={cn(TYPE.small, 'text-ds-red-ink')}>{t('patientReports.texts_error')}</p>
        ) : library.types.length === 0 ? (
          <SettingsEmpty
            icon={<Tag className="h-7 w-7" />}
            title={t('settings.integrations.patient_reports.text_types_empty')}
            action={
              canEdit ? (
                <Button type="button" onClick={openCreate} className="w-full">
                  <Plus className="h-4 w-4" aria-hidden />
                  {t('settings.integrations.patient_reports.text_types_new')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="-mx-s4 nav:-mx-s5">
            {library.types.map((type) => (
              <ListRow
                key={type.id}
                data-testid={`settings-text-type-${type.id}`}
                className="items-start justify-between"
              >
                <div className="min-w-0 flex-1">
                  <FileCategoryChip name={type.name} colorToken="muted" />
                  <Checkbox
                    className="mt-s3"
                    checked={type.singleVersion}
                    disabled={!canEdit || library.isSaving}
                    label={t('settings.integrations.patient_reports.text_types_single')}
                    onChange={(event) => void setFlag(type, event.target.checked)}
                  />
                </div>
                {canEdit ? (
                  <div className="flex shrink-0 gap-s1">
                    <Button
                      type="button"
                      variant="tertiary"
                      size="icon"
                      onClick={() => openEdit(type)}
                      aria-label={t('common.edit')}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="icon"
                      disabled={library.isSaving}
                      onClick={() => void remove(type)}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ) : null}
              </ListRow>
            ))}
          </div>
        )}
        {showForm ? (
          <Modal
            title={
              editing
                ? t('settings.integrations.patient_reports.text_types_edit')
                : t('settings.integrations.patient_reports.text_types_new')
            }
            onClose={closeForm}
          >
            <form onSubmit={(event) => void onSubmit(event)} className="space-y-s4">
              <Input
                id="report-text-type-name"
                label={t('settings.integrations.patient_reports.text_types_name')}
                hint={t('settings.integrations.patient_reports.text_types_placeholder')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                maxLength={80}
              />
              <Checkbox
                checked={singleVersion}
                label={t('settings.integrations.patient_reports.text_types_single')}
                hint={t('settings.integrations.patient_reports.text_types_single_hint')}
                onChange={(event) => setSingleVersion(event.target.checked)}
              />
              <div className="flex justify-end gap-s2 pt-s2">
                <Button type="button" variant="secondary" onClick={closeForm}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={library.isSaving || !name.trim()}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Modal>
        ) : null}
      </div>
    </SectionCard>
  );
}
